import { Badge } from '@common/components';
import { cn } from '@core/utils';

import FourLeafClover from '@/assets/svgs/attendance/four-leaf-clover.svg';
import RedFourLeafClover from '@/assets/svgs/attendance/red-four-leaf-clover.svg';
import SilverFourLeafClover from '@/assets/svgs/attendance/silver-four-leaf-clover.svg';
import TodayMarker from '@/assets/svgs/attendance/today-marker.svg';

import {
  CLOVER_CYCLE_LENGTH,
  type CloverDay,
  type CloverStatus,
  getCloverReward,
} from '../_utils/resolveCloverStrip';

const CLOVER_BONUS_BADGE: Partial<
  Record<number, { label: string; className: string }>
> = {
  2: { label: '2배', className: 'bg-O-300 text-Gray-white' },
  3: { label: '3배', className: 'bg-B-500 text-Gray-white' },
};

//* 오늘(진행중)은 아직 미출석이라 '비어있는' 실버로 두고 ▼로만 위치를 표시한다.
//* 빨강(red)은 '지나갔는데 출석하지 못한' 결석일에만 쓴다.
const CLOVER_ICON_BY_STATUS: Record<CloverStatus, typeof FourLeafClover> = {
  earned: FourLeafClover,
  today: SilverFourLeafClover,
  skipped: RedFourLeafClover,
  upcoming: SilverFourLeafClover,
};

const CLOVER_STATUS_LABEL: Record<CloverStatus, string> = {
  earned: '출석 완료',
  today: '오늘',
  skipped: '결석',
  upcoming: '예정',
};

//* 비회원은 사이클 데이터가 없어 7일 전부 upcoming(silver)으로 보여준다.
const GUEST_CLOVER_DAYS: CloverDay[] = Array.from(
  { length: CLOVER_CYCLE_LENGTH },
  (_, index): CloverDay => {
    const day = index + 1;

    return { day, reward: getCloverReward(day), status: 'upcoming' };
  },
);

interface AttendanceCloverStripProps {
  cloverDays?: CloverDay[];
  variant?: 'guest';
}

export const AttendanceCloverStrip = ({
  cloverDays,
  variant,
}: AttendanceCloverStripProps) => {
  //* 빈 배열도 폴백으로 본다 — 서버가 days를 안 내려주면 스트립이 통째로 사라져
  //* 박스만 남는다(`?? `만으로는 [] 를 통과시킨다).
  const days =
    variant === 'guest' || !cloverDays?.length ? GUEST_CLOVER_DAYS : cloverDays;

  return (
    <ul className="flex items-end justify-between rounded-small bg-Gray-50 px-16 py-20">
      {days.map((clover) => {
        const bonus = CLOVER_BONUS_BADGE[clover.reward];
        const CloverIcon = CLOVER_ICON_BY_STATUS[clover.status];
        const isToday = clover.status === 'today';

        return (
          <li
            key={clover.day}
            aria-label={`${clover.day}일차, ${CLOVER_STATUS_LABEL[clover.status]}`}
            className="relative flex items-center justify-center"
          >
            {/* 오늘 표시(▼)·보너스 배지는 absolute로 띄워 클로버 높이를 고정한다(CLS 방지).
                배지가 있는 날은 ▼를 배지 위로 더 올려 서로 겹치지 않게 한다. */}
            {isToday ? (
              <TodayMarker
                aria-hidden
                className={cn(
                  'absolute inset-x-0 z-20 mx-auto size-10',
                  bonus ? '-top-16' : '-top-12',
                )}
              />
            ) : null}
            {bonus ? (
              <Badge
                className={cn(
                  'absolute -right-3 -top-4 z-10 typo-caption2',
                  bonus.className,
                )}
              >
                {bonus.label}
              </Badge>
            ) : null}
            <CloverIcon aria-hidden className="size-36" />
          </li>
        );
      })}
    </ul>
  );
};
